import SectionLabel from "@/components/SectionLabel";
import EditorialBanner from "@/components/EditorialBanner";
import {
  Layers,
  Zap,
  ShieldCheck,
  Binary,
  EyeOff,
} from "lucide-react";

const FEATURES = [
  {
    icon: Layers,
    title: "No rip-and-replace overlay",
    body: "Sentinel sits on top of your existing KYC, AML, TM and fraud stack. Vendor-agnostic. No CIF migration.",
    hex: "#0047ff",
    soft: "rgba(0,71,255,0.10)",
  },
  {
    icon: Zap,
    title: "Live in under 3 months",
    body: "From signed paper to operational. Standard connectors, configuration-driven onboarding, no platform rebuild.",
    hex: "#f59e0b",
    soft: "rgba(245,158,11,0.14)",
  },
  {
    icon: Binary,
    title: "Deterministic output, always",
    body: "Same input, same output. Reproducible bit-for-bit for any auditor — no drift, no retraining cycles.",
    hex: "#00b8c9",
    soft: "rgba(0,229,255,0.14)",
  },
  {
    icon: EyeOff,
    title: "No black-box AI",
    body: "Zero black-box models in the scoring path. Every assessment ships with the drivers behind it.",
    hex: "#f43f5e",
    soft: "rgba(244,63,94,0.10)",
  },
  {
    icon: ShieldCheck,
    title: "Immutable data logging",
    body: "Append-only, hash-chained ledger of every assessment and decision. Tamper-evident. Replayable.",
    hex: "#10b981",
    soft: "rgba(16,185,129,0.10)",
  },
];

const Features = () => {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="dark-section relative overflow-hidden py-24 lg:py-32 border-t border-white/10"
    >
      <div className="absolute inset-0 bg-dot-grid opacity-40" />
      <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
          <div className="lg:col-span-7">
            <SectionLabel index="02" label="Features around the two outcomes" dark />
            <h2 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[58px] tracking-tight leading-[1.02]">
              How we make those two outcomes <br />
              <span className="text-white/55">defensible to a supervisor.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/65 text-[17px] leading-relaxed">
              Five structural features ring the product. Each is a precondition
              for taking Sentinel&rsquo;s false-positive reduction and entity
              resolution to a regulator with confidence.
            </p>
          </div>
        </div>

        <EditorialBanner
          gradient="indigo-rose"
          image="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=2400&q=80"
          kicker="Five features"
          headline="Engineered for the question a supervisor will ask."
          chips={[
            { c: "#0047ff", t: "Overlay" },
            { c: "#f59e0b", t: "Live < 3 mo" },
            { c: "#f43f5e", t: "No black-box AI" },
          ]}
          testId="features-banner"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`feature-${i}`}
                className="bg-[#0d0e16] p-6 lg:p-7 flex flex-col gap-5 min-h-[260px] relative"
              >
                <span
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ backgroundColor: f.hex }}
                />
                <div className="flex items-center justify-between">
                  <span
                    className="font-mono text-[10px] tracking-[0.22em] uppercase"
                    style={{ color: f.hex }}
                  >
                    Feature / 0{i + 1}
                  </span>
                  <span
                    className="inline-flex items-center justify-center w-10 h-10"
                    style={{ backgroundColor: f.soft }}
                  >
                    <Icon
                      className="w-5 h-5"
                      style={{ color: f.hex }}
                      strokeWidth={2}
                    />
                  </span>
                </div>
                <div className="mt-auto">
                  <p className="font-display font-bold text-white text-[17px] tracking-tight leading-tight">
                    {f.title}
                  </p>
                  <p className="mt-2 text-white/65 text-[13px] leading-relaxed">
                    {f.body}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
